<template>
  <div class="txn-wrap">

    <!-- ── Edit Modal ──────────────────────────────────────── -->
    <MDBModal
      :id="'editModal-' + heading"
      tabindex="-1"
      v-model="show_modal"
      centered
      @hidden="onModalClosed"
    >
      <div class="em-header">
        <span class="em-icon" :style="{ background: typeAccent }">
          <i :class="['fas', catIcon(selected_transaction.v3_category)]"></i>
        </span>
        <div class="em-title">
          <p class="em-txn-name">{{ selected_transaction.v3_name || 'Edit Transaction' }}</p>
          <p class="em-txn-amount">{{ displayAmt(selected_transaction) }}</p>
        </div>
        <button class="btn-close em-close" @click="cancelEdit"></button>
      </div>

      <MDBModalBody class="em-body">
        <div class="em-field">
          <label class="em-label">Date</label>
          <input type="date" v-model="selected_transaction.v3_date" class="em-input" />
        </div>
        <div class="em-field">
          <label class="em-label">Name</label>
          <input type="text" v-model="selected_transaction.v3_name" class="em-input" />
        </div>
        <div class="em-field">
          <label class="em-label">Amount</label>
          <input type="number" v-model="selected_transaction.v3_amount" class="em-input" step="0.01" />
        </div>
        <div class="em-field">
          <label class="em-label">Category</label>
          <MDBSelect
            v-model:selected="selected_transaction.v3_category"
            v-model:options="static_categories"
          />
        </div>
        <div class="em-field">
          <label class="em-label">Tags</label>
          <div class="em-tag-wrap">
            <span
              v-for="tag in (selected_transaction.v3_tags || [])"
              :key="tag"
              class="em-tag-chip"
            >
              {{ tag }}<button class="em-tag-x" @click.stop="removeTag(tag)">×</button>
            </span>
            <input
              v-model="tag_input"
              class="em-tag-input"
              placeholder="Add tag, press Enter…"
              @keydown.enter.prevent="addTag"
            />
          </div>
        </div>
        <div class="em-field">
          <label class="em-label">Note</label>
          <textarea
            v-model="selected_transaction.v3_notes"
            class="em-input em-notes"
            rows="2"
            placeholder="Add a note…"
          ></textarea>
        </div>
        <div class="em-field em-field--json">
          <button class="json-toggle" @click="show_raw_json = !show_raw_json">
            {{ show_raw_json ? '▾' : '▸' }} Raw JSON
          </button>
          <pre v-if="show_raw_json" class="json-blob">{{ JSON.stringify(transaction_before_edit, null, 2) }}</pre>
        </div>
        <div class="em-actions">
          <button class="em-btn em-cancel" @click="cancelEdit">Cancel</button>
          <button class="em-btn em-save" @click="acceptEdit">Save</button>
          <button class="em-btn em-delete" @click="onDeleteClick">Delete</button>
        </div>
      </MDBModalBody>
    </MDBModal>

    <!-- ── Total bar ───────────────────────────────────────── -->
    <div class="txn-total-bar">
      <span class="txn-count">{{ filteredData.length }} transaction{{ filteredData.length !== 1 ? 's' : '' }}</span>
      <span class="txn-total-amt">{{ fmtAmount(totalAmount) }}</span>
    </div>

    <!-- ── States ──────────────────────────────────────────── -->
    <div v-if="loading" class="txn-state">Loading…</div>
    <div v-else-if="filteredData.length === 0" class="txn-state">No transactions</div>

    <!-- ── Date-grouped list ────────────────────────────────── -->
    <div v-else class="txn-list">
      <template v-for="group in groupedData" :key="group.date">
        <div class="date-header">{{ fmtGroupDate(group.date) }}</div>
        <div
          v-for="txn in group.txns"
          :key="txn.date_transaction_id"
          class="txn-row"
          :class="{
            'txn-row--pending': txn.pending && !txn.v3_deleted,
            'txn-row--deleted': txn.v3_deleted,
          }"
          @click="openEdit(txn)"
        >
          <span class="txn-cat-icon">
            <i :class="['fas', catIcon(txn.v3_category)]"></i>
          </span>
          <div class="txn-body">
            <div class="txn-name-row">
              <span class="txn-name">{{ txn.v3_name }}</span>
              <span class="txn-amount" :class="amtClass">{{ displayAmt(txn) }}</span>
            </div>
            <div class="txn-meta-row">
              <span class="txn-cat-label">{{ catDisplayName(txn.v3_category) }}</span>
              <template v-if="txn.v3_account_name">
                <span class="txn-dot">·</span>
                <span class="txn-account">{{ txn.v3_account_name }}</span>
              </template>
              <span v-if="txn.pending && !txn.v3_deleted" class="txn-pending-chip">Pending</span>
            </div>
            <div
              class="txn-tags-row"
              v-if="(txn.v3_tags || []).length || txn.v3_notes"
            >
              <span
                v-for="tag in (txn.v3_tags || [])"
                :key="tag"
                class="txn-tag"
                @click.stop="$emit('tag-clicked', tag)"
              >{{ tag }}</span>
              <span v-if="txn.v3_notes" class="txn-note-dot" :title="txn.v3_notes">📝</span>
            </div>
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script>
import { reactive } from 'vue';
import { patch } from '../common/api';
import {
  MDBModal, MDBModalBody, MDBSelect,
} from 'mdb-vue-ui-kit';

const ICONS = {
  gym_memberships: 'fa-dumbbell',
  rent: 'fa-home',
  daily_travel: 'fa-subway',
  health_and_fitness: 'fa-heartbeat',
  car_payment: 'fa-car',
  utilities: 'fa-lightbulb',
  steep_hollow: 'fa-tree',
  subscriptions: 'fa-podcast',
  zogchow: 'fa-utensils',
  car_insurance: 'fa-shield-alt',
  clothing: 'fa-tshirt',
  discretionary: 'fa-store',
  car_maintenance: 'fa-wrench',
  other: 'fa-folder',
  entertainment: 'fa-mountain',
  groceries: 'fa-carrot',
};

const TYPE_ACCENTS = {
  expense: '#ef4444',
  income: '#10b981',
  investment: '#8b5cf6',
  transfer: '#f59e0b',
  cc_payment: '#3b82f6',
};

export default {
  name: 'BudgetItemGrid',
  components: { MDBModal, MDBModalBody, MDBSelect },
  props: {
    expense_categories: Array,
    data: Array,
    loading: Boolean,
    heading: String,
    selected_category: Object,
    month: Number,
    year: Number,
    type: String,
  },
  emits: ['transaction-selected', 'transaction-edited', 'transaction-deleted', 'transaction-added', 'show-detail', 'tag-clicked'],
  data(props) {
    return {
      gridtype: props.type,
      griddata: [],
      static_categories: [],
      selected_transaction: reactive({}),
      transaction_before_edit: reactive({}),
      show_modal: false,
      show_raw_json: false,
      edit_accepted: false,
      delete_clicked: false,
      tag_input: '',
    };
  },
  computed: {
    filteredData() {
      if (this.selected_category?.name) {
        return this.griddata.filter(t => t.v3_category === this.selected_category.name);
      }
      return this.griddata;
    },
    totalAmount() {
      return this.filteredData.reduce((sum, t) => {
        const amt = this.gridtype === 'income'
          ? Math.abs(t.v3_amount || 0)
          : (t.v3_amount || 0);
        return sum + amt;
      }, 0);
    },
    groupedData() {
      const groups = {};
      this.filteredData.forEach(t => {
        const key = t.v3_date || 'Unknown';
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      return Object.entries(groups)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([date, txns]) => ({ date, txns }));
    },
    amtClass() {
      if (this.gridtype === 'income') return 'amt-income';
      if (this.gridtype === 'investment') return 'amt-savings';
      return 'amt-expense';
    },
    typeAccent() {
      return TYPE_ACCENTS[this.gridtype] || '#6b7280';
    },
  },
  methods: {
    catIcon(name) { return ICONS[name] || 'fa-tag'; },
    catDisplayName(name) {
      return this.static_categories.find(c => c.value === name)?.text || name || '';
    },
    displayAmt(txn) {
      if (!txn?.v3_amount && txn?.v3_amount !== 0) return '';
      const raw = this.gridtype === 'income'
        ? Math.abs(txn.v3_amount)
        : txn.v3_amount;
      return '$' + Math.abs(raw).toFixed(2);
    },
    fmtAmount(val) {
      return '$' + Math.abs(val || 0).toFixed(2);
    },
    fmtGroupDate(dateStr) {
      if (!dateStr || dateStr === 'Unknown') return 'Unknown date';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });
    },

    // ── Tag helpers ───────────────────────────────────────────
    addTag() {
      const tag = this.tag_input.trim().toLowerCase();
      if (!tag) return;
      if (!Array.isArray(this.selected_transaction.v3_tags)) {
        this.selected_transaction.v3_tags = [];
      }
      if (!this.selected_transaction.v3_tags.includes(tag)) {
        this.selected_transaction.v3_tags.push(tag);
      }
      this.tag_input = '';
    },
    removeTag(tag) {
      this.selected_transaction.v3_tags =
        (this.selected_transaction.v3_tags || []).filter(t => t !== tag);
    },

    // ── Modal lifecycle ──────────────────────────────────────
    openEdit(txn) {
      this.transaction_before_edit = JSON.parse(JSON.stringify(txn));
      this.selected_transaction = txn;
      this.tag_input = '';
      this.static_categories.forEach(c => { c.selected = c.value === txn.v3_category; });
      this.$emit('transaction-selected', txn);
      this.show_raw_json = false;
      this.show_modal = true;
    },
    cancelEdit() {
      this.edit_accepted = false;
      this.show_modal = false;
    },
    acceptEdit() {
      this.addTag(); // commit any tag still in the input field
      this.edit_accepted = true;
      this.show_modal = false;
    },
    onDeleteClick() {
      this.delete_clicked = true;
      this.deleteTransaction(this.selected_transaction);
      this.show_modal = false;
    },
    onModalClosed() {
      if (this.delete_clicked) {
        // already handled in deleteTransaction
      } else if (this.edit_accepted) {
        this.selected_transaction.v3_amount = Number(this.selected_transaction.v3_amount);
        this.editTransaction(this.selected_transaction);
      } else {
        // Restore original values into the same array slot
        const idx = this.griddata.findIndex(
          t => t.date_transaction_id === this.transaction_before_edit.date_transaction_id
        );
        if (idx >= 0) {
          this.griddata.splice(idx, 1, JSON.parse(JSON.stringify(this.transaction_before_edit)));
        }
      }
      this.edit_accepted = false;
      this.delete_clicked = false;
    },

    // ── API calls ────────────────────────────────────────────
    editTransaction(transaction) {
      try {
        const store = JSON.parse(localStorage.getItem('txn_tags') || '{}');
        store[transaction.date_transaction_id] = transaction.v3_tags || [];
        localStorage.setItem('txn_tags', JSON.stringify(store));
      } catch (e) { /* ignore localStorage errors */ }
      this.$emit('transaction-edited', transaction);
      patch('transactions', transaction)
        .then(r => {
          if (r.status === 401) { localStorage.removeItem('token'); this.$router.push('/login'); throw new Error('Unauthorized'); }
          if (!r.ok) throw new Error('Failed to update');
        })
        .catch(err => { alert('Failed to update transaction'); console.error(err); });
    },
    deleteTransaction(transaction) {
      transaction.v3_deleted = true;
      patch('transactions', { date_transaction_id: transaction.date_transaction_id, v3_deleted: true })
        .then(r => {
          if (r.status === 401) { localStorage.removeItem('token'); this.$router.push('/login'); throw new Error('Unauthorized'); }
          if (!r.ok) throw new Error('Failed to delete');
        })
        .then(() => { this.$emit('transaction-deleted', transaction); })
        .catch(err => { alert('Failed to delete transaction'); console.error(err); });
    },
  },
  watch: {
    data(newData) {
      this.griddata = [...(newData || [])].sort((a, b) => new Date(b.v3_date) - new Date(a.v3_date));
    },
    expense_categories(newCats) {
      this.static_categories = JSON.parse(JSON.stringify(newCats || []));
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ──────────────────────────────────────────────── */
.txn-wrap {
  margin-bottom: 8px;
}

/* ── Total bar ────────────────────────────────────────────── */
.txn-total-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 20px;
  background: #f9fafb;
  border-top: 1px solid #f1f2f6;
  border-bottom: 1px solid #f1f2f6;
}
.txn-count {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}
.txn-total-amt {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

/* ── States ───────────────────────────────────────────────── */
.txn-state {
  padding: 24px;
  text-align: center;
  font-size: 14px;
  color: #9ca3af;
}

/* ── Date header ──────────────────────────────────────────── */
.date-header {
  padding: 8px 20px 4px;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: #fafafa;
  border-bottom: 1px solid #f1f2f6;
}

/* ── Transaction row ──────────────────────────────────────── */
.txn-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
  background: #fff;
}
.txn-row:hover,
.txn-row:active {
  background: #f9fafb;
}
.txn-row--pending {
  border-left: 3px solid #f59e0b;
  background: #fffbf0;
}
.txn-row--pending:hover {
  background: #fef9e7;
}
.txn-row--deleted {
  opacity: 0.45;
}
.txn-row--deleted .txn-name {
  text-decoration: line-through;
}

/* ── Icon ─────────────────────────────────────────────────── */
.txn-cat-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13px;
  color: #6b7280;
}

/* ── Body ─────────────────────────────────────────────────── */
.txn-body {
  flex: 1;
  min-width: 0;
}
.txn-name-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}
.txn-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.txn-amount {
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}
.amt-expense { color: #111827; }
.amt-income  { color: #10b981; }
.amt-savings { color: #8b5cf6; }

.txn-meta-row {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.txn-cat-label {
  font-size: 12px;
  color: #6b7280;
}
.txn-dot {
  font-size: 10px;
  color: #d1d5db;
}
.txn-account {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.txn-pending-chip {
  display: inline-block;
  padding: 1px 7px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
}

/* ── Tags on rows ────────────────────────────────────────── */
.txn-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.txn-tag {
  display: inline-block;
  padding: 1px 8px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s;
}
.txn-tag:hover { background: #c7d2fe; }
.txn-note-dot { font-size: 12px; cursor: default; }

/* ── Edit Modal ───────────────────────────────────────────── */
.em-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.em-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
}
.em-title {
  flex: 1;
  min-width: 0;
}
.em-txn-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.em-txn-amount {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}
.em-close {
  flex-shrink: 0;
}
.em-body {
  padding: 16px 20px;
}
.em-field {
  margin-bottom: 14px;
}
.em-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.em-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.em-input:focus {
  border-color: #4f8ef7;
}
.em-field--json {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.json-toggle {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  color: #9ca3af;
  cursor: pointer;
}
.json-toggle:hover { color: #6b7280; }
.json-blob {
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.72rem;
  line-height: 1.5;
  color: #374151;
  overflow-x: auto;
  white-space: pre;
}
.em-actions {
  display: flex;
  gap: 8px;
  padding-top: 4px;
}
.em-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.em-btn:hover { opacity: 0.85; }
.em-cancel  { background: #f3f4f6; color: #374151; }
.em-save    { background: #4f8ef7; color: #fff; }
.em-delete  { background: #fee2e2; color: #dc2626; margin-left: auto; }

/* Tag input inside modal */
.em-tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 6px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  min-height: 38px;
  background: #fff;
  cursor: text;
}
.em-tag-wrap:focus-within { border-color: #4f8ef7; }
.em-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
}
.em-tag-x {
  background: none;
  border: none;
  padding: 0;
  line-height: 1;
  font-size: 14px;
  color: #818cf8;
  cursor: pointer;
}
.em-tag-x:hover { color: #4338ca; }
.em-tag-input {
  border: none;
  outline: none;
  font-size: 13px;
  color: #374151;
  flex: 1;
  min-width: 100px;
  background: transparent;
}
.em-notes {
  resize: vertical;
  min-height: 60px;
}
</style>
